import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Search, Boxes, Package2, AlertTriangle, ChevronDown, ChevronRight, FolderTree, LayoutList, Rows3 } from 'lucide-react';
import { Header } from '@/components/layout/Header';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useBetonProduits, useCatalogueGroupes } from '@/hooks/useBetonCatalog';
import { ProduitFormDialog } from '@/components/operations/beton/ProduitFormDialog';
import { GroupesCatalogueDialog } from '@/components/operations/beton/GroupesCatalogueDialog';
import { FAMILLE_LABELS, type FamilleProduit, type Produit } from '@/types';
import { formatFCFA, formatNumber } from '@/utils/currency';

/** Vue mémorisée sur l'appareil (simple confort : la page marche sans). */
type Vue = 'groupe' | 'liste';
const CLE_VUE = 'catalogue.vue';
const lireVue = (): Vue => { try { return localStorage.getItem(CLE_VUE) === 'liste' ? 'liste' : 'groupe'; } catch { return 'groupe'; } };
const ecrireVue = (v: Vue) => { try { localStorage.setItem(CLE_VUE, v); } catch { /* stockage indisponible */ } };

const SANS_GROUPE = 'aucun';

const CarteProduit = ({ p, avecGroupe }: { p: Produit; avecGroupe: boolean }) => {
  // La disponibilité vient du parc, pas de `stockActuel` — ce dernier est un cumul de production qui ne redescend jamais.
  const dispoNum = p.disponibleParc ?? 0;
  const stockLow = dispoNum < parseFloat(p.seuilAlerte);
  return (
    <Link to={`/operations/catalogue/${p.id}`}>
      <Card className={!p.actif ? 'opacity-60' : ''} accent={p.actif}>
        <CardContent className="!pt-5 space-y-2">
          <div className="flex items-start justify-between">
            <div className="min-w-0 flex-1">
              <p className="font-mono text-[11px] text-gray-400">{p.code}</p>
              <h3 className="truncate text-sm font-semibold text-gray-900">{p.designation}</h3>
            </div>
            {!p.actif && <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] uppercase text-gray-500">Inactif</span>}
          </div>
          <div className="flex flex-wrap gap-1">
            <span className="inline-block rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-gray-600">
              {FAMILLE_LABELS[p.famille]}
            </span>
            {avecGroupe && (
              <span className="inline-block rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-medium text-sky-800">{p.groupe?.libelle ?? 'Non classé'}</span>
            )}
            {p.classeBeton && (
              <span className="inline-block rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-800">
                {p.classeBeton.replace('c', 'C').replace('_', '/')}
              </span>
            )}
            {p.delaiCureJours != null && (
              <span className="inline-block rounded-full bg-amber-100 px-2 py-0.5 text-[10px] text-amber-800">Cure {p.delaiCureJours}j</span>
            )}
          </div>
          {(p.longueurM || p.largeurMm || p.hauteurMm) && (
            <p className="text-[11px] text-gray-400 tabular-nums">
              {p.longueurM ? `L ${p.longueurM}m` : ''}
              {p.largeurMm ? ` · l ${p.largeurMm}mm` : ''}
              {p.hauteurMm ? ` · h ${p.hauteurMm}mm` : ''}
              {p.epaisseurMm ? ` · ép ${p.epaisseurMm}mm` : ''}
              {p.poidsKg ? ` · ${parseFloat(p.poidsKg).toFixed(1)} kg` : ''}
            </p>
          )}
          <div className="border-t border-gray-100 pt-2">
            <p className="text-xs text-gray-500">Prix de vente</p>
            <p className="text-base font-semibold tabular-nums text-gray-900">{formatFCFA(p.prixVenteCents)}</p>
            <p className="text-[11px] text-gray-400">par {p.uniteVente}</p>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-500">Disponible au parc</span>
            <span className={stockLow ? 'flex items-center gap-1 font-semibold text-amber-600' : 'tabular-nums text-gray-700'}>
              {stockLow && <AlertTriangle className="size-3" />}
              {formatNumber(dispoNum)} {p.uniteVente}
            </span>
          </div>
          {(p.reserveParc ?? 0) > 0 && (
            <div className="flex items-center justify-between text-[11px]">
              <span className="text-gray-400">dont réservé</span>
              <span className="tabular-nums text-gray-500">{formatNumber(p.reserveParc)} {p.uniteVente}</span>
            </div>
          )}
        </CardContent>
      </Card>
    </Link>
  );
};

const GRILLE = 'grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4';
const SELECT = 'flex h-10 w-full rounded-md border border-gray-200 bg-white px-3 text-sm';

export const CataloguePage = () => {
  const [search, setSearch] = useState('');
  const [famille, setFamille] = useState<FamilleProduit | ''>('');
  const [groupe, setGroupe] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [groupesOpen, setGroupesOpen] = useState(false);
  const [vue, setVue] = useState<Vue>(lireVue);
  const [replies, setReplies] = useState<Set<string>>(new Set());

  const groupesQ = useCatalogueGroupes();
  const groupes = useMemo(() => groupesQ.data?.groupes ?? [], [groupesQ.data]);
  const { data, isLoading, error } = useBetonProduits({
    search: search || undefined,
    famille: famille || undefined,
    groupeId: groupe || undefined,
    actif: showInactive ? undefined : true,
    limit: 500,
  });

  // Sections dans l'ordre défini, « Non classés » en dernier ; une section vide n'est pas affichée.
  const sections = useMemo(() => {
    const produits = data?.data ?? [];
    const parGroupe = new Map<string, Produit[]>();
    for (const p of produits) {
      const k = p.groupeId ?? SANS_GROUPE;
      parGroupe.set(k, [...(parGroupe.get(k) ?? []), p]);
    }
    return [
      ...groupes.map((g) => ({ id: g.id, titre: g.libelle, actif: g.actif, produits: parGroupe.get(g.id) ?? [] })),
      { id: SANS_GROUPE, titre: 'Non classés', actif: true, produits: parGroupe.get(SANS_GROUPE) ?? [] },
    ].filter((s) => s.produits.length > 0);
  }, [data, groupes]);

  const basculer = (id: string) => setReplies((r) => { const n = new Set(r); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const changerVue = (v: Vue) => { setVue(v); ecrireVue(v); };
  const libelleGroupe = groupe === SANS_GROUPE ? 'Non classés' : groupes.find((g) => g.id === groupe)?.libelle;

  return (
    <>
      <Header
        title="Catalogue produits"
        subtitle="Catalogue dynamique — créer, modifier, suivre stock et coût de revient"
        actions={
          <>
            <Link to="/operations/matieres">
              <Button variant="secondary" size="sm">
                <Package2 className="size-4" /> Matières premières
              </Button>
            </Link>
            <Button variant="secondary" size="sm" onClick={() => setGroupesOpen(true)}>
              <FolderTree className="size-4" /> Gérer les groupes
            </Button>
            <Button size="sm" onClick={() => setDialogOpen(true)}>
              <Plus className="size-4" /> Nouveau produit
            </Button>
          </>
        }
      />

      <div className="space-y-4 p-4 sm:p-6">
        {/* Filtres */}
        <Card>
          <CardContent className="!pt-5">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-[1fr_190px_190px_auto_auto]">
              <div className="relative sm:col-span-2 lg:col-span-1">
                <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Rechercher par code, désignation ou groupe…"
                  className="pl-9"
                />
              </div>
              <select value={famille} onChange={(e) => setFamille(e.target.value as FamilleProduit | '')} className={SELECT} aria-label="Famille">
                <option value="">Toutes les familles</option>
                {(Object.keys(FAMILLE_LABELS) as FamilleProduit[]).map((f) => (
                  <option key={f} value={f}>{FAMILLE_LABELS[f]}</option>
                ))}
              </select>
              <select value={groupe} onChange={(e) => setGroupe(e.target.value)} className={SELECT} aria-label="Groupe">
                <option value="">Tous les groupes</option>
                {groupes.map((g) => (
                  <option key={g.id} value={g.id}>{g.libelle}{g.actif ? '' : ' (désactivé)'}</option>
                ))}
                <option value={SANS_GROUPE}>Non classés</option>
              </select>
              <div className="inline-flex rounded-md border border-gray-200 bg-white p-0.5" role="group" aria-label="Affichage">
                <button type="button" onClick={() => changerVue('groupe')} aria-pressed={vue === 'groupe'}
                  className={`flex items-center gap-1 rounded px-2.5 text-xs ${vue === 'groupe' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-100'}`}>
                  <Rows3 className="size-3.5" /> Par groupe
                </button>
                <button type="button" onClick={() => changerVue('liste')} aria-pressed={vue === 'liste'}
                  className={`flex items-center gap-1 rounded px-2.5 text-xs ${vue === 'liste' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-100'}`}>
                  <LayoutList className="size-3.5" /> Liste simple
                </button>
              </div>
              <label className="flex items-center gap-2 px-2 text-xs text-gray-600">
                <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
                Inclure inactifs
              </label>
            </div>
          </CardContent>
        </Card>

        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            Erreur de chargement du catalogue
          </div>
        )}

        {/* Stats */}
        {data && (
          <div className="flex items-center justify-between text-sm text-gray-500">
            <span>
              <strong className="text-gray-900">{data.meta.total}</strong> produit{data.meta.total > 1 ? 's' : ''}
              {famille && ` · ${FAMILLE_LABELS[famille as FamilleProduit]}`}
              {libelleGroupe && ` · ${libelleGroupe}`}
            </span>
            {vue === 'groupe' && sections.length > 1 && (
              <button type="button" className="text-xs text-gray-500 hover:text-gray-800"
                onClick={() => setReplies((r) => (r.size ? new Set() : new Set(sections.map((s) => s.id))))}>
                {replies.size ? 'Tout déplier' : 'Tout replier'}
              </button>
            )}
          </div>
        )}

        {isLoading && (
          <div className={GRILLE}>
            {Array.from({ length: 6 }).map((_, i) => (
              <Card key={i}><CardContent className="!pt-5 h-32 animate-pulse bg-gray-50" /></Card>
            ))}
          </div>
        )}

        {!isLoading && data?.data.length === 0 && (
          <Card>
            <CardContent className="!pt-12 pb-12 text-center">
              <Boxes className="mx-auto size-10 text-gray-300" />
              <p className="mt-3 text-sm text-gray-600">
                {search || famille || groupe ? 'Aucun produit ne correspond à ces filtres' : 'Aucun produit dans le catalogue'}
              </p>
              {!(search || famille || groupe) && (
                <>
                  <p className="mt-1 text-xs text-gray-400">Commencez par créer une référence ou importer un fichier</p>
                  <Button className="mt-4" onClick={() => setDialogOpen(true)}>
                    <Plus className="size-4" /> Créer le premier produit
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {!isLoading && data && data.data.length > 0 && vue === 'liste' && (
          <div className={GRILLE}>{data.data.map((p) => <CarteProduit key={p.id} p={p} avecGroupe />)}</div>
        )}

        {!isLoading && data && data.data.length > 0 && vue === 'groupe' && (
          <div className="space-y-5">
            {sections.map((s) => {
              const replie = replies.has(s.id);
              return (
                <section key={s.id} aria-label={s.titre}>
                  <button type="button" onClick={() => basculer(s.id)} aria-expanded={!replie}
                    className="mb-3 flex w-full items-center gap-2 border-b border-gray-200 pb-2 text-left">
                    {replie ? <ChevronRight className="size-4 text-gray-400" /> : <ChevronDown className="size-4 text-gray-400" />}
                    <h2 className="text-sm font-semibold text-gray-900">{s.titre}</h2>
                    {!s.actif && <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] uppercase text-gray-500">Désactivé</span>}
                    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] tabular-nums text-gray-600">{s.produits.length}</span>
                  </button>
                  {!replie && <div className={GRILLE}>{s.produits.map((p) => <CarteProduit key={p.id} p={p} avecGroupe={false} />)}</div>}
                </section>
              );
            })}
          </div>
        )}
      </div>

      <ProduitFormDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
      <GroupesCatalogueDialog open={groupesOpen} onClose={() => setGroupesOpen(false)} />
    </>
  );
};
